import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Modal, ModalContent } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { Ellipsis, FileDown, Lock, Mic, Pencil, Play, Quote, Trash2 } from 'lucide-react';

import { estimateWorkoutMinutes, formatSeconds, isWarmupEntry } from '../../utils/workoutStats';
import { ExerciseArt, ExerciseWhat, WarmupTag } from './parts';
import { baseName, countSets, formatLoad, useExerciseLine } from './exerciseUtils';
import { PreviewArt } from '../app/PreviewArt';

const view = (t, key, options) => t(`pages.Workouts.view.${key}`, options);
const list = (t, key, options) => t(`pages.Workouts.list.${key}`, options);

/** The ⋯ menu: the gym sheet (PDF) and deleting, which asks first */
const MoreMenu = ({ title, isExporting, isLocked, onExport, onDelete }) => {
  const { t } = useTranslation();
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    setDeleting(true);
    const isDeleted = await onDelete();
    setDeleting(false);
    // On failure the dialog stays open; the page shows the error
    if (isDeleted) setConfirmOpen(false);
  };

  return (
    <>
      <Dropdown placement="bottom-end" classNames={{ content: 'mt-root mt-menu' }}>
        <DropdownTrigger>
          <button type="button" className="mt-tr-icon-btn mt-tr-icon-btn--lg" aria-label={list(t, 'more', { name: title })}>
            <Ellipsis aria-hidden="true" />
          </button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label={list(t, 'more', { name: title })}
          disabledKeys={isExporting || isLocked ? ['export'] : []}
          itemClasses={{ base: 'mt-menu__item' }}
          onAction={(key) => (key === 'export' ? onExport() : setConfirmOpen(true))}
        >
          <DropdownItem key="export" startContent={<FileDown aria-hidden="true" />}>
            {list(t, 'exportSheet')}
          </DropdownItem>
          <DropdownItem key="delete" className="mt-menu__item--danger" startContent={<Trash2 aria-hidden="true" />}>
            {list(t, 'delete')}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
      <Modal
        isOpen={isConfirmOpen}
        onOpenChange={setConfirmOpen}
        placement="center"
        hideCloseButton
        classNames={{ base: 'mt-root mt-dialog', backdrop: 'mt-dialog__backdrop' }}
      >
        <ModalContent>
          {(onClose) => (
            <div className="mt-dialog__body">
              <h2 className="mt-dialog__title">{list(t, 'deleteTitle', { name: title })}</h2>
              <p className="mt-dialog__text">{list(t, 'deleteBody')}</p>
              <div className="mt-dialog__actions">
                <button type="button" className="mt-btn mt-btn--quiet" onClick={onClose} disabled={isDeleting}>
                  {list(t, 'cancel')}
                </button>
                <button type="button" className="mt-btn mt-btn--danger" onClick={confirmDelete} disabled={isDeleting}>
                  {list(t, 'confirmDelete')}
                </button>
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

/**
 * One workout, read before it's edited: the big numbers, the sheet (sets × reps, load, rest) and Start. The pane
 * beside the list on desktop; its own page on phones. A workout kept from an ended Pro subscription comes without
 * its exercises (the API keeps them back): it shows the way back to Pro instead.
 */
export const WorkoutView = ({ workout, library, isDesktop, isExporting, unlockLabel, onStart, onExport, onDelete, onUnlock, onEdit }) => {
  const { t, i18n: { language } } = useTranslation();
  const line = useExerciseLine();
  const title = list(t, 'workoutName', { name: workout.name });
  const { exercises, isLocked } = workout;
  const titleId = `mt-tr-view-${workout.id}`;

  const head = (
    <div className="mt-tr-view__top">
      <span className="mt-tr-letter mt-tr-letter--lg" aria-hidden="true">
        {workout.name}
      </span>
      <div className="mt-tr-view__names">
        <h2 id={titleId} className="mt-tr-view__title" tabIndex={-1}>
          {title}
          {workout.isNext && !isLocked && <span className="mt-tr-pill mt-tr-pill--lg">{list(t, 'today')}</span>}
          {isLocked && (
            <span className="mt-tr-pill mt-tr-pill--lg mt-tr-pill--kept">
              <Lock aria-hidden="true" />
              {list(t, 'lockedTag')}
            </span>
          )}
        </h2>
        {workout.description && <p className="mt-tr-view__muscles">{workout.description}</p>}
      </div>
    </div>
  );

  const menu = (
    <MoreMenu
      title={title}
      isExporting={isExporting}
      isLocked={isLocked}
      onExport={() => onExport(workout.id)}
      onDelete={() => onDelete(workout.id)}
    />
  );

  const actions = isLocked ? (
    <div className="mt-tr-view__acts mt-tr-view__acts--locked">{menu}</div>
  ) : (
    <div className="mt-tr-view__acts">
      <button type="button" className="mt-tr-btn mt-tr-btn--start" onClick={() => onStart(workout.id)}>
        <Play aria-hidden="true" />
        {isDesktop ? view(t, 'start') : view(t, 'startShort')}
      </button>
      <Link className="mt-tr-btn mt-tr-btn--quiet mt-tr-btn--lg" to={`/workout/${workout.id}`} state={{ fromView: true }} onClick={onEdit}>
        <Pencil aria-hidden="true" />
        {list(t, 'edit')}
      </Link>
      {menu}
    </div>
  );

  if (isLocked) {
    return (
      <article className="mt-tr-view" aria-labelledby={titleId}>
        {isDesktop ? (
          <div className="mt-tr-view__grid">
            {head}
            {actions}
          </div>
        ) : (
          <>
            {head}
            {actions}
          </>
        )}
        <section className="mt-tr-locked" aria-labelledby="mt-tr-locked-title">
          <PreviewArt kind="aiWorkout" badge="lock" />
          <h3 id="mt-tr-locked-title" className="mt-tr-locked__title">
            {view(t, 'lockedTitle')}
          </h3>
          <p className="mt-tr-locked__text">{view(t, 'lockedBody')}</p>
          <button type="button" className="mt-tr-btn mt-tr-btn--go" onClick={onUnlock}>
            {unlockLabel}
          </button>
        </section>
      </article>
    );
  }

  return (
    <article className="mt-tr-view" aria-labelledby={titleId}>
      {isDesktop ? (
        <div className="mt-tr-view__grid">
          {head}
          {actions}
        </div>
      ) : (
        head
      )}

      <dl className="mt-tr-stats">
        <div>
          <dt>{view(t, 'statExercises', { count: exercises.length })}</dt>
          <dd>{exercises.length}</dd>
        </div>
        <div>
          <dt>{view(t, 'statSets', { count: countSets(exercises) })}</dt>
          <dd>{countSets(exercises)}</dd>
        </div>
        <div>
          <dt>{view(t, 'statMinutes')}</dt>
          <dd>~{estimateWorkoutMinutes(exercises)}</dd>
        </div>
      </dl>

      {!isDesktop && actions}

      <section aria-labelledby={`${titleId}-sheet`}>
        <div className="mt-tr-sheet-head">
          <h3 id={`${titleId}-sheet`}>{view(t, 'exercises')}</h3>
          {!isDesktop && <span>{view(t, 'inOrder')}</span>}
        </div>
        {/* A table on desktop (exercise · sets × reps · load · rest); rows with the numbers in a line on phones */}
        {isDesktop ? (
          <table className="mt-tr-table">
            <thead>
              <tr>
                <th scope="col">
                  <span className="mt-sr-only">{view(t, 'colArt')}</span>
                </th>
                <th scope="col">{view(t, 'colExercise')}</th>
                <th scope="col">{view(t, 'colSetsReps')}</th>
                <th scope="col">{view(t, 'colLoad')}</th>
                <th scope="col">{view(t, 'colRest')}</th>
              </tr>
            </thead>
            <tbody>
              {exercises.map((exercise, index) => {
                const entry = exercise.exerciseId ? library.get(exercise.exerciseId) : null;
                const load = formatLoad(exercise, language);
                return (
                  <tr key={`${index}-${exercise.name}`} className={isWarmupEntry(exercise) ? 'is-warm' : undefined}>
                    <td>
                      <ExerciseArt src={exercise.thumbnailUrl} />
                    </td>
                    <td>
                      <span className="mt-tr-table__name">
                        {baseName(exercise)}
                        {isWarmupEntry(exercise) && <WarmupTag />}
                      </span>
                      {entry ? (
                        <ExerciseWhat muscle={entry.muscleGroup} equipment={entry.equipmentCategory} className="mt-tr-table__what" />
                      ) : (
                        // Typed by hand: there's no library entry to say what it works
                        <span className="mt-tr-table__what">{exercise.exerciseId ? '' : view(t, 'notInLibrary')}</span>
                      )}
                    </td>
                    <td className="mt-tr-num">
                      {exercise.series} × {exercise.reps}
                    </td>
                    <td className={`mt-tr-num${load ? '' : ' mt-tr-num--dim'}`}>{load || view(t, 'noLoad')}</td>
                    <td className="mt-tr-num">{formatSeconds(Number(exercise.interval) || 60)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <ol className="mt-tr-xrows">
            {exercises.map((exercise, index) => (
              <li key={`${index}-${exercise.name}`} className={`mt-tr-xrow${isWarmupEntry(exercise) ? ' is-warm' : ''}`}>
                <ExerciseArt src={exercise.thumbnailUrl} />
                <span className="mt-tr-xrow__text">
                  <span className="mt-tr-xrow__name">
                    {baseName(exercise)}
                    {isWarmupEntry(exercise) && <WarmupTag />}
                  </span>
                  <span className="mt-tr-xrow__line">{line(exercise)}</span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <p className="mt-tr-prefs">
        <span className={workout.voiceInstructions ? '' : 'is-off'}>
          <Mic aria-hidden="true" />
          {view(t, workout.voiceInstructions ? 'voiceOn' : 'voiceOff')}
        </span>
        <span className={workout.motivation ? '' : 'is-off'}>
          <Quote aria-hidden="true" />
          {view(t, workout.motivation ? 'motivationOn' : 'motivationOff')}
        </span>
      </p>
    </article>
  );
};
